import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../../../trpc.js";
import {
  Title,
  Stack,
  Group,
  ActionIconGroup,
  ActionIcon,
} from "@mantine/core";
import { useNavigateMap } from "../../../routes/app/map/hooks/navigate.js";
import { useNavigateTileset } from "../../../routes/app/tileset/hooks/navigate.js";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { Carousel } from "@mantine/carousel";
import { QuickAccessCard, type Resource } from "./QuickAccessCard.js";
import type { EmblaCarouselType } from "embla-carousel";

export function QuickStart() {
  const intl = useIntl();
  const trpc = useTRPC();

  const {
    data: userActivities,
    isLoading,
  } = useQuery(trpc.getUserActivities.queryOptions());

  const navigateMap = useNavigateMap();
  const navigateTileset = useNavigateTileset();

  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);

  const handleCarouselNext = useCallback(() => {
    if (!embla) return;
    const indexesOfVisibleSlides = embla.slidesInView();
    const indexOfNextSlide = Math.max(...indexesOfVisibleSlides) + 1;
    embla.scrollTo(indexOfNextSlide);
  }, [embla]);

  const handleCarouselPrevious = useCallback(() => {
    if (!embla) return;
    const indexesOfVisibleSlides = embla.slidesInView();
    const indexOfPreviousSlide = Math.max(Math.min(...indexesOfVisibleSlides) - 1, 0);
    embla.scrollTo(indexOfPreviousSlide);
  }, [embla]);

  if (!userActivities?.length) {
    return null;
  }

  const previousLabel = intl.formatMessage({
    defaultMessage: "Previous",
    id: "label.previous",
  });

  const nextLabel = intl.formatMessage({
    defaultMessage: "Next",
    id: "label.next",
  });

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={3}>
          <FormattedMessage defaultMessage="Continue your work" id="title.continueWork" />
        </Title>
        <ActionIconGroup style={{ gap: 8 }}>
          <ActionIcon
            onClick={handleCarouselPrevious}
            size={40}
            variant="light"
            aria-label={previousLabel}
          >
            <IconArrowNarrowLeft size={20} />
          </ActionIcon>
          <ActionIcon
            onClick={handleCarouselNext}
            size={40}
            variant="light"
            aria-label={nextLabel}
          >
            <IconArrowNarrowRight size={20} />
          </ActionIcon>
        </ActionIconGroup>
      </Group>
      <Carousel
        slideSize={320}
        slideGap={24}
        withControls={false}
        withIndicators={false}
        getEmblaApi={setEmbla}
        withKeyboardEvents={false}
      >
        {userActivities.map(activity => (
          <Carousel.Slide
            key={activity.object.id}
            pb={5}
          >
            <QuickAccessCard
              isLoading={isLoading}
              onNavigate={() => {
                if (activity.type === "map") {
                  navigateMap(activity.account.name, activity.object.key);
                }
                else if (activity.type === "tileset") {
                  navigateTileset(activity.account.name, activity.object.key);
                }
              }}
              resource={activity as unknown as Resource}
            />
          </Carousel.Slide>
        ))}
      </Carousel>
    </Stack>
  );
};
